import React, { useEffect, useRef, useState } from 'react'
import { useParams, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import eventsApi from 'api/requests/events';
import eventSectorsApi from 'api/requests/eventSectors';
import { useEvent } from 'context/events/Event';
import { Column } from 'styled/alignment/Column';
import Navbar from 'components/commons/Navbar';
import PageCover from './cover/PageCover';
import Loader from 'components/_UI/Loader';
import PageContent from './content/PageContent';
import breakpoints from 'theme/breakpoints';
import { FullyCentered } from 'styled/alignment/Center';
import Ilustration from 'components/_UI/Ilustration';
import Text from 'components/_UI/Text';
import Button from 'components/_UI/Button';
import paths from 'routes/paths';
import FooterCheckout from 'components/_UI/FooterCheckout';
import Footer from 'components/commons/Footer';
import BookingMap from './content/BookingMap';
import Sectors from './content/Sectors';
import routeParams from 'theme/routeParams';
import { Row } from 'styled/alignment/Row';
import faqApi from 'api/requests/faq';
import pixelsApi from 'api/requests/pixels';
import { injectPixels, cleanupPixels } from 'scripts/pixels';
import { useAuth } from 'context/auth/Auth';
import participantLikesApi from 'api/requests/participantLikes';
import useToast from 'hooks/useToast';
import ToastContainer from 'components/_UI/ToastsContainer';


export default function FullPage() {
  const { slug } = useParams();
  const { event, setEvent } = useEvent();
  const [loading, setLoading] = useState(true);
  const [showError, setShowError] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const scrollTo = searchParams.get(routeParams.events.scroll);
  const [eventSectors, setEventSectors] = useState([]);
  const containerRef = useRef();
  const [faq, setFaq] = useState([]);
  const [participants, setParticipants] = useState([]);
  const [publicParticipants, setPublicParticipants] = useState([]);
  const [participantsBlocked, setParticipantsBlocked] = useState(false);
  const { isAuth, user } = useAuth();
  const [likesStatus, setLikesStatus] = useState(null);
  const { toasts, toast } = useToast();


useEffect(() => {
  if (slug) {
    getEvent(slug);
    getEventSectors(slug);
    getFaq(slug);
    getPixels(slug);
    getParticipantsPreview(slug);
    if (isAuth) {
      getPublicParticipants(slug);
      getLikesStatus(slug);
    }
  }

  return () => cleanupPixels();
}, [slug]);

 useEffect(() => {
    if (scrollTo && eventSectors?.length) {
      setTimeout(() => {
        containerRef?.current?.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'start' 
        });
      }, 400);
    }
  }, [scrollTo, eventSectors]);

  async function getFaq(slug) {
    setLoading(true);
    try {
      const { data } = await faqApi.get(slug);
      setFaq(data?.data)
    } catch {

    } finally {
      setLoading(false);
    }
  }
  async function getPixels(slug) {
    try {
      const { data } = await pixelsApi.getPublic(slug);
      injectPixels(data?.data);
    } catch {
    }
  }

  async function getEvent(slug) {
    setLoading(true);
    try {
      const { data } = await eventsApi.getBySlug(slug);
      setEvent(data?.data);
    } catch ({ response }) {
      if (response?.status == 404) {
        setShowError(true)
      }
    } finally {
      setLoading(false);
    }
  }

  async function getParticipantsPreview(slug) {
    try {
      const { data } = await eventsApi.getParticipantsPreview(slug);
      setParticipants(data?.data || []);
    } catch {
    }
  }

  async function getPublicParticipants(slug) {
    try {
      const { data } = await eventsApi.getParticipantsPublic(slug);
      setPublicParticipants(data?.data || []);
    } catch (error) {
      if (error?.response?.status === 403) {
        setParticipantsBlocked(true);
      }
    }
  }

  async function getEventSectors(slug) {
    setLoading(true);
    const { data } = await eventSectorsApi.get(slug);
    setEventSectors(data?.data);
    setLoading(false);
  }

  async function getLikesStatus(slug) {
    try {
      const { data } = await participantLikesApi.getStatus(slug);
      setLikesStatus(data?.data || data);
    } catch {
    }
  }

  async function handleLikeParticipant(participantId) {
    const previousStatus = likesStatus;
    const previousParticipants = publicParticipants;

    setLikesStatus(prev => ({
      ...prev,
      remaining_likes: prev.remaining_likes - 1,
      liked_user_ids: [...(prev.liked_user_ids || []), participantId],
    }));
    setPublicParticipants(prev =>
      prev.map(p => p.id === participantId ? { ...p, is_liked: true } : p)
    );

    try {
      const { data } = await participantLikesApi.like(slug, participantId);
      const response = data?.data || data;

      setLikesStatus(prev => ({
        ...prev,
        remaining_likes: response.remaining_likes,
        ...(response.is_match && {
          matches: [...(prev.matches || []), participantId],
        }),
      }));

      if (response.is_match) {
        setPublicParticipants(prev =>
          prev.map(p => p.id === participantId ? { ...p, is_match: true } : p)
        );
        toast.success('Deu match! 💜');
      } else {
        toast.success('Curtida enviada!');
      }
    } catch {
      setLikesStatus(previousStatus);
      setPublicParticipants(previousParticipants);
      toast.error('Erro ao curtir participante.');
    }
  }


  return (
    <Container>
      <Navbar />
      {
        loading ? (
          <FullyCentered marginTop='200px'>
            <Loader size='large' />
          </FullyCentered>
        ) : (
          showError ? (
            <Column fullyCentralized marginTop='100px' paddingLeft='10px' paddingRight='10px'>
              <Column height='250px' marginBottom='30px'>
                <Ilustration name='not-found' />
              </Column>
              <Text name='highlight' textAlign={'center'}>Não encontramos nenhum resultado na sua busca.</Text>
              <Text name='highlight' textAlign={'center'}>Que tal explorar novas expêriencias?</Text>
              <Column width='300px' marginTop='20px'>
                <Button variant='featured' path={paths.events.home}>Buscar expêriencias</Button>
              </Column>
            </Column>
          ) : (
            <>
              {
                !!event && (
                  <>
                    <PageCover data={event} participants={participants} />
                    <PageContainer>
                      <PageContent
                        data={event}
                        faq={faq}
                        participants={publicParticipants}
                        isAuth={isAuth}
                        participantsBlocked={participantsBlocked}
                        likesStatus={likesStatus}
                        currentUserId={user?.id}
                        onLikeParticipant={handleLikeParticipant}
                      />
                      {
                        !!eventSectors && !!eventSectors.length &&
                          <ContentSector ref={containerRef}>
                            <Sectors
                              sectors={eventSectors}
                              event={event}
                            />
                          </ContentSector>
                      }
                      {
                        !!event?.address && (
                          <BookingMap data={event} />
                        )
                      }
                      <FooterCheckout />
                    </PageContainer>
                  </>
                )
              }
              <Footer />
            </>
          )
        )
      }
      <ToastContainer toasts={toasts} />
    </Container >
  );
}

const Container = styled(Column)`
  width: 100%;
  
  @media screen and (min-width: ${breakpoints.lg}) {
    margin-top: 100px;
  }
`;

const PageContainer = styled.div`
  width: 100%;
  padding: 0 1%;
  
  @media screen and (min-width: ${breakpoints.lg}) {
    padding: 0 6%;
    
  }
`;

const ContentSector = styled(Column)`
  
 
  
`